// src/lib/dashboard/painelCeo.ts
//
// Contas da aba "CEO" do Painel Executivo (pedido do Igor em 19/10/2026): só a
// ponta da informação — dinheiro do mês, usinas contra a meta e o que parou as
// usinas. Cada número aqui reaproveita a regra que o resto do sistema já usa
// (status do boleto, parte da Vigor, meta PVsyst, interrupções); o que é novo é
// só o recorte por usina.
//
// Função pura, sem I/O nem JSX: roda sob `node --test` com type-stripping.

import type { FaturaPainel } from '../financeiro/types.ts';
import { valorDevido } from '../statusBoleto.ts';
import { ehCobranca, ehContaInterna, estaPago, faturasDoPainel, dataVencimentoDe } from '../financeiro/definicoesKpi.ts';

// ─────────────────────────── usinas ───────────────────────────

/** Uma usina da Vigor, como o CEO conhece: Vera Cruz 01, Morro Agudo 03... */
export interface UsinaCeo {
  /** `rateio_unidades_geradoras.id` — é a usina do contrato com a distribuidora. */
  id: string;
  nome: string;
  conjunto: string;
  /** Números da UC geradora (novo e antigo), só dígitos e sem zero à esquerda. */
  ucs: string[];
  /** `unidades_geradoras.site_id_api` da telemetria. `null` = usina sem monitoramento. */
  siteId: string | null;
  capacidadeKwp: number | null;
}

export interface RateioUgLinha {
  id: string;
  nome: string;
  ativo: boolean | null;
  capacidade_kwp: number | null;
  telemetria_site_id: string | null;
  numero_uc_geradora: string | null;
  numero_uc_geradora_antigo: string | null;
}

export interface UnidadeGeradoraLinha {
  site_id_api: string;
  nome_usina: string | null;
  /** MWp. */
  capacidade_instalada: number | null;
}

/** "000406019101257" e "4.060.191.012-57" viram o mesmo "406019101257". */
export const digitosUc = (v: unknown): string => String(v ?? '').replace(/\D/g, '').replace(/^0+/, '');

/**
 * "Usina Fazenda Vera Cruz 01" → Vera Cruz 01. "Usina Cooperativa Centro-Sul - UC
 * 10022969932" → Centro-Sul ·9932 (as três do Centro-Sul só se distinguem pela UC).
 */
export function nomeDaUsina(nomeCadastro: string): { nome: string; conjunto: string } {
  const limpo = nomeCadastro.replace(/^usina\s+/i, '').replace(/^(fazenda|cooperativa)\s+/i, '').trim();
  const comUc = limpo.match(/^(.*?)\s*-\s*UC\s*(\d+)\s*$/i);
  if (comUc) {
    const conjunto = comUc[1].trim();
    return { nome: `${conjunto} ·${comUc[2].slice(-4)}`, conjunto };
  }
  const conjunto = limpo.replace(/\s+\d+$/, '').trim() || limpo;
  return { nome: limpo, conjunto };
}

/**
 * A lista de usinas sai do RATEIO, não da telemetria: é lá que está a UC
 * geradora que as faturas dos clientes citam. A telemetria é ligada por
 * `telemetria_site_id` e, quando falta (Morro Agudo em 19/10/2026), pelo número
 * da UC escrito no nome do inversor ("Qgbt1 - UC - 4.085.442.012-82").
 *
 * Usina inativa fica de fora — "Morrinhos (duplicada da Vera Cruz 03 - não
 * usar)" é inativa justamente para não aparecer duas vezes.
 */
export function montarUsinas(rateio: RateioUgLinha[], unidades: UnidadeGeradoraLinha[]): UsinaCeo[] {
  const porSite = new Map(unidades.map((u) => [u.site_id_api, u]));
  const ucNoNome = (u: UnidadeGeradoraLinha) =>
    (u.nome_usina?.match(/\d[\d.]*\.\d{3}-\d{2}/g) ?? []).map(digitosUc);

  return rateio
    .filter((r) => r.ativo !== false)
    .map((r) => {
      const ucs = [r.numero_uc_geradora, r.numero_uc_geradora_antigo].map(digitosUc).filter(Boolean);
      const telemetria =
        (r.telemetria_site_id ? porSite.get(r.telemetria_site_id) : undefined) ??
        unidades.find((u) => ucNoNome(u).some((d) => ucs.includes(d)));
      const { nome, conjunto } = nomeDaUsina(r.nome);
      const capTelemetria = Number(telemetria?.capacidade_instalada);
      return {
        id: r.id,
        nome,
        conjunto,
        ucs,
        siteId: telemetria?.site_id_api ?? null,
        capacidadeKwp:
          Number.isFinite(capTelemetria) && capTelemetria > 0
            ? capTelemetria * 1000
            : Number(r.capacidade_kwp) > 0
              ? Number(r.capacidade_kwp)
              : null,
      };
    })
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { numeric: true }));
}

// ─────────────────────────── geração x meta ───────────────────────────

/** O que a aba precisa de uma linha de `get_geracao_vs_meta`. */
export interface LinhaVsMeta {
  site_id_api: string;
  geracao_kwh: number | null;
  /** Meta PVsyst já rateada pelos dias decorridos. `null` = usina sem laudo. */
  meta_kwh: number | null;
  dias_decorridos: number;
  /** 1º dia do mês (ISO). Só a série de vários meses precisa. */
  mes_ref?: string;
  /** Havia leitura antes do mês: sem isso os primeiros dias somem e o mês não é comparável. */
  tem_baseline?: boolean;
  /** Dias com contador resetado: energia perdida de fato, mês não comparável. */
  baldes_nulos?: number;
  /** Meta do mês inteiro, sem rateio. */
  meta_cheia_kwh?: number | null;
}

export interface GeracaoUsina {
  usinaId: string;
  realKwh: number;
  metaKwh: number | null;
  /** `true` = sem laudo PVsyst próprio: a meta foi emprestada das usinas com laudo. */
  metaEstimada: boolean;
  /** 0,9 = 90% da meta até hoje. */
  atingimento: number | null;
  semTelemetria: boolean;
}

/**
 * Produtividade esperada até hoje, em kWh por kWp, tirada das usinas que TÊM
 * laudo: soma das metas rateadas ÷ soma das potências. `null` quando nenhuma
 * usina da lista tem laudo no período.
 */
export function produtividadeDoLaudo(usinas: UsinaCeo[], linhas: LinhaVsMeta[]): number | null {
  const porSite = new Map(linhas.map((l) => [l.site_id_api, l]));
  let meta = 0;
  let kwp = 0;
  for (const u of usinas) {
    const l = u.siteId ? porSite.get(u.siteId) : undefined;
    const laudo = Number(l?.meta_kwh);
    if (!l || !(laudo > 0) || !u.capacidadeKwp) continue;
    meta += laudo;
    kwp += u.capacidadeKwp;
  }
  return kwp > 0 ? meta / kwp : null;
}

/**
 * Realizado contra a meta ATÉ HOJE — "até aquele dia do mês a usina deveria ter
 * gerado 50.000 kWh e gerou 45.000: está com 90%".
 *
 * Só as 5 usinas de Vera Cruz têm laudo PVsyst cadastrado (19/10/2026). Para as
 * outras, decisão do usuário no mesmo dia: usar o laudo delas como parâmetro —
 * a produtividade esperada do mês (kWh por kWp, `produtividadeDoLaudo`) vezes a
 * potência da usina. É a mesma região e o mesmo sol, e a meta acompanha a
 * sazonalidade do laudo em vez de um fator fixo o ano inteiro. A linha diz que
 * a meta é estimada. Sem laudo nenhum no período, cai no fator kWh/kWp/dia de
 * Config, o mesmo do bloco Geradoras da Visão geral.
 */
export function geracaoPorUsina(usinas: UsinaCeo[], linhas: LinhaVsMeta[], fatorKwhKwpDia: number): GeracaoUsina[] {
  const porSite = new Map(linhas.map((l) => [l.site_id_api, l]));
  const produtividade = produtividadeDoLaudo(usinas, linhas);
  return usinas.map((u) => {
    const l = u.siteId ? porSite.get(u.siteId) : undefined;
    if (!l) return { usinaId: u.id, realKwh: 0, metaKwh: null, metaEstimada: false, atingimento: null, semTelemetria: true };
    const real = Number(l.geracao_kwh) || 0;
    const laudo = Number(l.meta_kwh);
    const temLaudo = Number.isFinite(laudo) && laudo > 0;
    const meta = temLaudo
      ? laudo
      : !u.capacidadeKwp
        ? null
        : produtividade != null
          ? u.capacidadeKwp * produtividade
          : fatorKwhKwpDia > 0
            ? u.capacidadeKwp * fatorKwhKwpDia * Math.max(0, l.dias_decorridos)
            : null;
    return {
      usinaId: u.id,
      realKwh: real,
      metaKwh: meta,
      metaEstimada: !temLaudo && meta != null,
      atingimento: meta && meta > 0 ? real / meta : null,
      semTelemetria: false,
    };
  });
}

/** Total só das usinas que têm meta: somar geração sem meta inflaria o percentual. */
export function totalGeracao(lista: GeracaoUsina[]): { realKwh: number; metaKwh: number; atingimento: number | null } {
  const comMeta = lista.filter((g) => g.metaKwh != null && g.metaKwh > 0);
  const realKwh = comMeta.reduce((s, g) => s + g.realKwh, 0);
  const metaKwh = comMeta.reduce((s, g) => s + (g.metaKwh ?? 0), 0);
  return { realKwh, metaKwh, atingimento: metaKwh > 0 ? realKwh / metaKwh : null };
}

/**
 * Faixas do atingimento. As mesmas de `classificarAtingimento` da tela de
 * Geradoras (95% = dentro do P90 do laudo), usadas também na meta financeira
 * para a aba ter UMA régua de cor.
 */
export type FaixaMeta = 'acima' | 'no_alvo' | 'abaixo' | 'critico' | 'sem_meta';

export function faixaDoAtingimento(atingimento: number | null): FaixaMeta {
  if (atingimento == null) return 'sem_meta';
  if (atingimento >= 1) return 'acima';
  if (atingimento >= 0.95) return 'no_alvo';
  if (atingimento >= 0.85) return 'abaixo';
  return 'critico';
}

// ─────────────────────────── paradas ───────────────────────────

/** Um episódio de parada que CONTA como perda (já filtrado pelo motivo). */
export interface EpisodioParado {
  siteId: string;
  inicio: string;
  fim: string;
  /** Horas de parada total equivalentes DENTRO do período (parcial a 75% por 8 h = 2 h). */
  horas: number;
  /** Rótulo do motivo registrado; `null` = ninguém classificou ainda. */
  motivo: string | null;
  /** Energia que deixou de ser gerada, estimada pela RPC, dentro do período. */
  energiaKwh?: number;
  /** 'parada' = não gerou; 'parcial' = gerou menos que as irmãs (inversor fora). */
  tipo?: 'parada' | 'parcial';
  emAberto?: boolean;
  /** Títulos dos alarmes do inversor durante o episódio, já traduzidos pelo catálogo. */
  alarmes?: string[];
  /** Só em 'parcial': quanto gerou, em % das irmãs. */
  pctReferencia?: number | null;
}

export type CausaParada = 'registrado' | 'rede' | 'inversor' | 'alarme' | 'sem_explicacao';

export const ROTULO_CAUSA: Record<CausaParada, string> = {
  registrado: 'Motivo registrado',
  rede: 'Queda da rede',
  inversor: 'Inversor fora',
  alarme: 'Alarme no inversor',
  sem_explicacao: 'Sem explicação',
};

/** Uma parada como o CEO lê: quando, quais usinas, quanto tempo, quanto perdeu e por quê. */
export interface EventoParada {
  chave: string;
  inicio: string;
  fim: string;
  emAberto: boolean;
  usinaIds: string[];
  /** Duração da parada (a do episódio mais longo do grupo), em horas de sol. */
  duracaoH: number;
  /** Soma das horas de todas as usinas do grupo: é o que entra no total do mês. */
  horasUsina: number;
  energiaKwh: number;
  causa: CausaParada;
  /** O motivo em poucas palavras: "Provável queda da rede", "Falha de inversor". */
  titulo: string;
  /** Uma frase de porquê, para quem nunca viu a tela de Geradoras. */
  explicacao: string;
}

const UMA_HORA = 3_600_000;

/**
 * Junta os episódios em PARADAS e diz por que cada uma aconteceu.
 *
 * O registro manual de motivo quase não é usado (149 h sem motivo em outubro de
 * 2026), então a explicação sai do que a telemetria já mostra, nesta ordem:
 *
 *   1. motivo registrado pela equipe — vale sobre tudo;
 *   2. várias usinas do MESMO conjunto parando juntas (início com até 1 h de
 *      diferença): queda da rede, não defeito de uma usina — as cinco de Vera
 *      Cruz pararam juntas em 06/10/2026;
 *   3. operação parcial (gerou menos que as irmãs): inversor fora;
 *   4. alarme do inversor durante a parada;
 *   5. nada disso: parou sozinha, sem alarme — fica como "sem explicação".
 *
 * Os casos 2 a 4 são DEDUÇÃO e a tela diz "provável".
 */
export function agruparParadas(episodios: EpisodioParado[], usinas: UsinaCeo[]): EventoParada[] {
  const usinaDoSite = new Map(usinas.filter((u) => u.siteId).map((u) => [u.siteId!, u]));
  const porConjunto = new Map<string, number>();
  usinas.forEach((u) => { if (u.siteId) porConjunto.set(u.conjunto, (porConjunto.get(u.conjunto) ?? 0) + 1); });

  const validos = episodios
    .filter((e) => e.horas > 0 && usinaDoSite.has(e.siteId))
    .sort((a, b) => Date.parse(a.inicio) - Date.parse(b.inicio));

  type Grupo = { conjunto: string; inicio: number; eps: EpisodioParado[] };
  const grupos: Grupo[] = [];
  for (const e of validos) {
    const u = usinaDoSite.get(e.siteId)!;
    const ini = Date.parse(e.inicio);
    const g = e.tipo === 'parcial'
      ? undefined
      : grupos.find((x) => x.conjunto === u.conjunto && x.eps[0].tipo !== 'parcial'
          && Math.abs(x.inicio - ini) <= UMA_HORA && !x.eps.some((y) => y.siteId === e.siteId));
    if (g) g.eps.push(e);
    else grupos.push({ conjunto: u.conjunto, inicio: ini, eps: [e] });
  }

  return grupos.map((g) => {
    const ids = [...new Set(g.eps.map((e) => usinaDoSite.get(e.siteId)!.id))];
    const motivos = g.eps.map((e) => e.motivo).filter((m): m is string => !!m);
    const alarmes = [...new Set(g.eps.flatMap((e) => e.alarmes ?? []))];
    const doConjunto = porConjunto.get(g.conjunto) ?? 0;
    const juntas = ids.length >= 2 && (ids.length === doConjunto || ids.length >= 3);
    const parcial = g.eps[0].tipo === 'parcial';

    let causa: CausaParada;
    let titulo: string;
    let explicacao: string;
    if (motivos.length) {
      causa = 'registrado';
      titulo = motivos[0];
      explicacao = 'Motivo registrado pela equipe de operação.';
    } else if (juntas) {
      causa = 'rede';
      titulo = 'Provável queda da rede';
      explicacao = `${ids.length === doConjunto ? `As ${ids.length} usinas de ${g.conjunto}` : `${ids.length} usinas de ${g.conjunto}`} pararam ao mesmo tempo — sinal de que faltou energia na rede da distribuidora, não de defeito numa usina.`;
    } else if (parcial) {
      causa = 'inversor';
      titulo = 'Provável inversor fora';
      const pct = g.eps[0].pctReferencia;
      explicacao = pct != null && pct > 0 && pct < 100
        ? `Gerou ${Math.round(100 - pct)}% menos que as usinas vizinhas no mesmo sol — o típico de um inversor desligado.`
        : 'Gerou menos que as usinas vizinhas no mesmo sol — o típico de um inversor desligado.';
      if (alarmes.length) explicacao += ` Alarme: ${alarmes.slice(0, 2).join(', ')}.`;
    } else if (alarmes.length) {
      causa = 'alarme';
      titulo = alarmes[0];
      explicacao = `O inversor avisou durante a parada${alarmes.length > 1 ? ` (também: ${alarmes.slice(1, 3).join(', ')})` : ''}.`;
    } else {
      causa = 'sem_explicacao';
      titulo = 'Parou sem explicação';
      explicacao = 'Parou sozinha, sem alarme no inversor, e ninguém registrou o motivo ainda.';
    }

    const fins = g.eps.map((e) => Date.parse(e.fim));
    return {
      chave: `${g.conjunto}|${g.eps[0].inicio}|${g.eps[0].siteId}`,
      inicio: g.eps[0].inicio,
      fim: new Date(Math.max(...fins)).toISOString(),
      emAberto: g.eps.some((e) => e.emAberto),
      usinaIds: ids,
      duracaoH: Math.max(...g.eps.map((e) => e.horas)),
      horasUsina: g.eps.reduce((t, e) => t + e.horas, 0),
      energiaKwh: g.eps.reduce((t, e) => t + (e.energiaKwh ?? 0), 0),
      causa,
      titulo,
      explicacao,
    };
  }).sort((a, b) => b.energiaKwh - a.energiaKwh || b.horasUsina - a.horasUsina);
}

export interface HorasPorCausa {
  causa: CausaParada;
  horas: number;
  parte: number;
}

/** "KPI de motivos": horas de usina parada por causa, da maior para a menor. */
export function horasPorCausa(eventos: EventoParada[]): HorasPorCausa[] {
  const acc = new Map<CausaParada, number>();
  eventos.forEach((e) => acc.set(e.causa, (acc.get(e.causa) ?? 0) + e.horasUsina));
  const total = [...acc.values()].reduce((t, h) => t + h, 0);
  return [...acc.entries()]
    .map(([causa, horas]) => ({ causa, horas, parte: total > 0 ? horas / total : 0 }))
    .sort((a, b) => b.horas - a.horas);
}

// ─────────────────────────── faturamento e energia por usina ───────────────────────────

/** A fatura com o que a aba lê a mais: a geração da UG impressa na conta do cliente. */
export type FaturaComUsina = FaturaPainel & {
  geracao_unidade_geradora_1?: number | null;
  /** Na conta DA PRÓPRIA usina: o que ela consumiu da rede, pago com os próprios créditos. */
  consumo_ativo_kwh?: number | null;
};

export interface FinanceiroUsina {
  /** `null` = faturas que não vêm de nenhuma usina da Vigor (avulsas, parcelas, usina de terceiro). */
  usinaId: string | null;
  /** Parte da Vigor nas cobranças do mês — o "realizado" da meta. */
  faturado: number;
  boletos: number;
  /** Energia que os clientes da usina compensaram nas contas do mês. */
  compensadaKwh: number;
  /** Energia que a usina injetou no ciclo, como a distribuidora imprime nas contas. */
  injetadaKwh: number | null;
  /** compensada ÷ injetada. Acima de 1 = os clientes estão gastando saldo acumulado. */
  uso: number | null;
  /** Consumo da conta da própria usina no mês. `null` = a conta dela não está no sistema. */
  consumoProprioKwh: number | null;
}

/**
 * Valor que se repete mais vezes, ignorando zero.
 *
 * A conta de cada cliente imprime a injeção da UG no ciclo DELE; clientes com
 * leitura em datas diferentes veem números um pouco diferentes (Vera Cruz 02,
 * SET/2026: 187.355 kWh em 127 contas, 194.672 em 25, 177.675 em 11). O ciclo
 * da maioria é o da usina.
 */
function valorMaisFrequente(valores: number[]): number | null {
  const cont = new Map<number, number>();
  for (const v of valores) {
    if (!(v > 0)) continue;
    const r = Math.round(v);
    cont.set(r, (cont.get(r) ?? 0) + 1);
  }
  let melhor: number | null = null;
  let vezes = 0;
  for (const [v, n] of cont) {
    if (n > vezes || (n === vezes && melhor != null && v > melhor)) { melhor = v; vezes = n; }
  }
  return melhor;
}

/**
 * Faturamento, energia injetada e uso, por usina, no mês de referência.
 *
 * A fatura é da usina cuja UC geradora ela cita em `unidade_geradora_1`. O valor
 * é a parte da Vigor (`parteVigor`, a mesma regra da previsão de recebíveis): em
 * cliente de empresa parceira entra só a fatia da Vigor, sem a Equatorial.
 *
 * O que não é de usina nenhuma da Vigor vai para uma linha só (`usinaId: null`),
 * para o total da aba bater com o faturamento do mês.
 */
export function financeiroPorUsina(
  faturas: FaturaComUsina[],
  usinas: UsinaCeo[],
  mesReferencia: string,
  parteVigor: (f: FaturaPainel) => number,
): FinanceiroUsina[] {
  const porUc = new Map<string, string>();
  usinas.forEach((u) => u.ucs.forEach((uc) => porUc.set(uc, u.id)));

  const acc = new Map<string | null, { faturado: number; boletos: number; compensada: number; geracoes: number[]; consumoProprio: number | null }>();
  const de = (id: string | null) => {
    let a = acc.get(id);
    if (!a) { a = { faturado: 0, boletos: 0, compensada: 0, geracoes: [], consumoProprio: null }; acc.set(id, a); }
    return a;
  };

  // A conta da própria Associação sai do FATURADO (não é receita de cliente),
  // mas não da ENERGIA: as UCs dela também gastam os créditos da usina, e
  // tirá-las fazia a Vera Cruz 01 parecer usar 10% do que injeta, em vez de 26%.
  for (const f of faturas) {
    if (f.mes_referencia !== mesReferencia) continue;
    const id = porUc.get(digitosUc(f.unidade_geradora_1)) ?? null;
    const a = de(id);
    if (!ehContaInterna(f) && ehCobranca(f)) {
      a.faturado += parteVigor(f);
      a.boletos += 1;
    }
    // A conta da PRÓPRIA usina (a UC dela é a geradora): o consumo dela é o
    // "consumo próprio", não energia mandada para cliente. Duas contas no mês
    // (reemissão) ficam com a maior, para não somar o mesmo consumo duas vezes.
    const propria = id !== null && usinas.find((u) => u.id === id)!.ucs.includes(digitosUc(f.uc_codigo));
    if (propria) {
      a.consumoProprio = Math.max(a.consumoProprio ?? 0, Number(f.consumo_ativo_kwh) || 0);
      continue;
    }
    a.compensada += (Number(f.energia_injetada_gd1_kwh) || 0) + (Number(f.energia_injetada_gd2_kwh) || 0);
    a.geracoes.push(Number(f.geracao_unidade_geradora_1) || 0);
  }

  const linha = (id: string | null): FinanceiroUsina => {
    const a = acc.get(id);
    if (!a) return { usinaId: id, faturado: 0, boletos: 0, compensadaKwh: 0, injetadaKwh: null, uso: null, consumoProprioKwh: null };
    const injetada = id ? valorMaisFrequente(a.geracoes) : null;
    return {
      usinaId: id,
      faturado: a.faturado,
      boletos: a.boletos,
      compensadaKwh: a.compensada,
      injetadaKwh: injetada,
      uso: injetada && injetada > 0 ? a.compensada / injetada : null,
      consumoProprioKwh: a.consumoProprio,
    };
  };

  return [...usinas.map((u) => linha(u.id)), linha(null)];
}

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** "SET/2026" ↔ ordinal (ano × 12 + mês 0-11), para andar mês a mês. */
export const ordinalDoMes = (mesRef: string): number | null => {
  const m = mesRef.match(/^([A-Z]{3})\/(\d{4})$/);
  if (!m) return null;
  const i = MESES.indexOf(m[1]);
  return i < 0 ? null : Number(m[2]) * 12 + i;
};
export const mesDoOrdinal = (ord: number): string => `${MESES[((ord % 12) + 12) % 12]}/${Math.floor(ord / 12)}`;
/** "SET/2026" → "2026-09-01", o formato de `vigente_desde`. */
export const isoDoMes = (mesRef: string): string | null => {
  const ord = ordinalDoMes(mesRef);
  if (ord == null) return null;
  return `${Math.floor(ord / 12)}-${String((ord % 12) + 1).padStart(2, '0')}-01`;
};

/**
 * O último mês de referência que já está todo faturado.
 *
 * As contas de um mês chegam ao longo do mês seguinte: em 19/10/2026 havia 442
 * faturas de OUT/2026 contra 1.135 de SET. Abrir a aba no mês corrente mostraria
 * toda usina "abaixo da meta" só porque a conta ainda não chegou. Fechado = tem
 * pelo menos 85% das faturas do mês anterior.
 */
export function ultimoMesFechado(faturas: FaturaPainel[], hoje: Date): string {
  const cont = new Map<number, number>();
  for (const f of faturas) {
    const ord = f.mes_referencia ? ordinalDoMes(f.mes_referencia) : null;
    if (ord != null) cont.set(ord, (cont.get(ord) ?? 0) + 1);
  }
  const atual = hoje.getFullYear() * 12 + hoje.getMonth();
  for (let ord = atual; ord > atual - 6; ord--) {
    const n = cont.get(ord) ?? 0;
    const anterior = cont.get(ord - 1) ?? 0;
    if (n > 0 && n >= anterior * 0.85) return mesDoOrdinal(ord);
  }
  return mesDoOrdinal(atual - 1);
}

export interface MetaFaturamentoLinha {
  rateio_unidade_geradora_id: string;
  /** "YYYY-MM-01". */
  vigente_desde: string;
  meta_brl: number;
}

/** A meta que valia no mês: a linha mais recente que começou até ele. 0 = sem meta. */
export function metaVigente(metas: MetaFaturamentoLinha[], usinaId: string, mesIso: string): number | null {
  let melhor: MetaFaturamentoLinha | null = null;
  for (const m of metas) {
    if (m.rateio_unidade_geradora_id !== usinaId) continue;
    const desde = String(m.vigente_desde).slice(0, 10);
    if (desde > mesIso) continue;
    if (!melhor || desde > String(melhor.vigente_desde).slice(0, 10)) melhor = m;
  }
  const v = Number(melhor?.meta_brl);
  return Number.isFinite(v) && v > 0 ? v : null;
}

// ─────────────────────────── dinheiro do período ───────────────────────────

export interface DinheiroDoPeriodo {
  /** Boletos que vencem no período — o que a Vigor espera receber. */
  esperado: number;
  boletos: number;
  /** Desses, os já pagos (em qualquer data). */
  recebido: number;
  /** Desses, os que venceram e não foram pagos. */
  vencido: number;
  /** Desses, os que ainda vão vencer. */
  aVencer: number;
  /** vencido ÷ (recebido + vencido): do que já venceu, quanto não entrou. `null` = nada venceu ainda. */
  inadimplencia: number | null;
  /** Fração do emitido que historicamente entra (meses já maduros). */
  taxaRecebimento: number;
  /** recebido + a vencer até `limitePrevisao` × taxa. `null` quando não há nada a vencer. */
  previsao: number | null;
  aVencerNoPrazo: number;
  /** Último dia útil do último mês do período: boleto que vence depois paga no mês seguinte. */
  limitePrevisao: string | null;
  /** Pagamentos que CAÍRAM no período, de qualquer boleto — o caixa pela data do pagamento. */
  caixa: number;
  pagamentos: number;
}

const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const somaMeses = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, d.getDate());

/** Usado só quando não há histórico maduro — o mesmo padrão da previsão de recebíveis. */
const TAXA_PADRAO = 0.844;

/**
 * Esperado x recebido de qualquer período — "o que estava esperado e o que
 * recebeu, neste mês, no mês passado ou no período que eu quiser" (pedido de
 * 19/10/2026).
 *
 * O recorte é pelo VENCIMENTO: o esperado do período são os boletos que vencem
 * nele, e cada um cai em um balde só — pago, vencido sem pagar ou a vencer. As
 * três partes somam o esperado, e a inadimplência sai da mesma conta: do que já
 * venceu, quanto não entrou. Um número para cada pergunta, todos do mesmo grupo
 * de boletos.
 *
 * O CAIXA (pagamentos pela data em que caíram) vai separado: é outra pergunta —
 * o boleto de setembro pago em outubro é caixa de outubro, mas era esperado em
 * setembro.
 *
 * Fatura-mãe de parcelamento fica fora: a dívida virou parcelas, que entram por
 * conta própria — contar as duas seria esperar o mesmo dinheiro duas vezes.
 *
 * `inicio`/`fim` em ISO (`fim` inclusivo); `null` nos dois = todo o histórico.
 * `limitePrevisao` é o último dia útil do último mês do período.
 */
export function calcularDinheiroDoPeriodo(
  entrada: FaturaPainel[],
  inicio: string | null,
  fim: string | null,
  hoje: Date,
  limitePrevisao: string | null,
): DinheiroDoPeriodo {
  const dados = faturasDoPainel(entrada);
  const hojeIso = isoLocal(hoje);
  const venc = (f: FaturaPainel) => String(dataVencimentoDe(f) ?? '').slice(0, 10);
  const noPeriodo = (d: string) => !!d && (!inicio || d >= inicio) && (!fim || d <= fim);

  let esperado = 0, boletos = 0, recebido = 0, vencido = 0, aVencer = 0, aVencerNoPrazo = 0;
  let caixa = 0, pagamentos = 0, maduroEmitido = 0, maduroPago = 0;
  const inicioMaduro = isoLocal(somaMeses(hoje, -14));
  const fimMaduro = isoLocal(somaMeses(hoje, -2));

  for (const f of dados) {
    const pgto = String(f.data_pgto_boleto ?? '').slice(0, 10);
    if (estaPago(f) && noPeriodo(pgto) && pgto <= hojeIso) {
      caixa += valorDevido(f);
      pagamentos += 1;
    }
    if (!ehCobranca(f) || f.statusDerivado === 'Parcelado') continue;
    const v = venc(f);
    if (!v) continue;

    if (v >= inicioMaduro && v < fimMaduro) {
      maduroEmitido += valorDevido(f);
      if (estaPago(f)) maduroPago += valorDevido(f);
    }
    if (!noPeriodo(v)) continue;

    const valor = valorDevido(f);
    if (estaPago(f)) recebido += valor;
    else if (f.statusDerivado === 'Vencido') vencido += valor;
    else if (f.statusDerivado === 'Em aberto') {
      aVencer += valor;
      if (!limitePrevisao || v <= limitePrevisao) aVencerNoPrazo += valor;
    } else continue;
    esperado += valor;
    boletos += 1;
  }

  const taxaRecebimento = maduroEmitido > 0 ? maduroPago / maduroEmitido : TAXA_PADRAO;
  const venceu = recebido + vencido;
  return {
    esperado,
    boletos,
    recebido,
    vencido,
    aVencer,
    inadimplencia: venceu > 0 ? vencido / venceu : null,
    taxaRecebimento,
    previsao: aVencer > 0 ? recebido + aVencerNoPrazo * taxaRecebimento : null,
    aVencerNoPrazo,
    limitePrevisao,
    caixa,
    pagamentos,
  };
}

export interface MesDinheiro extends DinheiroDoPeriodo {
  /** "OUT/2026". */
  mes: string;
  /** Ainda tem boleto a vencer: a inadimplência desse mês vai mudar. */
  emAndamento: boolean;
}

/** Teto da série: mais que isso vira uma régua ilegível no gráfico. */
export const MESES_MAXIMOS_SERIE = 24;

/**
 * O mesmo esperado x recebido, mês a mês — a "evolução" do gráfico. Cada mês
 * usa exatamente a conta do resumo (`calcularDinheiroDoPeriodo`), então a soma
 * dos meses bate com o total do período.
 *
 * `inicio`/`fim` em ISO; `null` = todo o histórico, que começa no vencimento
 * mais antigo e fica limitado aos últimos `MESES_MAXIMOS_SERIE` meses.
 */
export function serieMensalDinheiro(
  entrada: FaturaPainel[],
  inicio: string | null,
  fim: string | null,
  hoje: Date,
): MesDinheiro[] {
  let ini = inicio;
  if (!ini) {
    let menor: string | null = null;
    for (const f of entrada) {
      const v = String(dataVencimentoDe(f) ?? '').slice(0, 10);
      if (v && (!menor || v < menor)) menor = v;
    }
    ini = menor;
  }
  if (!ini) return [];
  const ultimo = fim ?? isoLocal(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0));

  const ordIni = Number(ini.slice(0, 4)) * 12 + Number(ini.slice(5, 7)) - 1;
  const ordFim = Number(ultimo.slice(0, 4)) * 12 + Number(ultimo.slice(5, 7)) - 1;
  const primeiro = Math.max(ordIni, ordFim - MESES_MAXIMOS_SERIE + 1);

  const saida: MesDinheiro[] = [];
  for (let ord = primeiro; ord <= ordFim; ord++) {
    const ano = Math.floor(ord / 12);
    const m = ord % 12;
    const de = isoLocal(new Date(ano, m, 1));
    const ate = isoLocal(new Date(ano, m + 1, 0));
    const d = calcularDinheiroDoPeriodo(entrada, de, ate, hoje, null);
    saida.push({ ...d, mes: mesDoOrdinal(ord), emAndamento: d.aVencer > 0 });
  }
  return saida;
}

// ─────────────────────────── geração mês a mês ───────────────────────────

export interface CelulaGeracao {
  realKwh: number;
  metaKwh: number | null;
  atingimento: number | null;
  estimada: boolean;
  /** `false` = mês sem dado completo (telemetria começou no meio, contador resetou). */
  comparavel: boolean;
}

export interface MatrizGeracao {
  /** "YYYY-MM-01", do mais antigo para o mais novo. */
  meses: string[];
  porUsina: Record<string, Record<string, CelulaGeracao>>;
  porConjunto: Record<string, Record<string, CelulaGeracao>>;
  total: Record<string, CelulaGeracao>;
}

/**
 * Usina × mês: quanto gerou contra a meta em cada mês. Cada mês usa a mesma
 * conta do mês corrente (`geracaoPorUsina`) — inclusive a meta emprestada do
 * laudo de Vera Cruz, que acompanha a estação do ano. Totais por conjunto e
 * geral só somam mês comparável com meta, para um mês de telemetria pela
 * metade não parecer usina ruim.
 */
export function matrizGeracao(usinas: UsinaCeo[], linhas: LinhaVsMeta[], fatorKwhKwpDia: number): MatrizGeracao {
  const meses = [...new Set(linhas.map((l) => String(l.mes_ref ?? '').slice(0, 10)).filter(Boolean))].sort();
  const porUsina: MatrizGeracao['porUsina'] = {};
  const porConjunto: MatrizGeracao['porConjunto'] = {};
  const total: MatrizGeracao['total'] = {};

  const somar = (alvo: Record<string, CelulaGeracao>, mes: string, c: CelulaGeracao) => {
    if (!c.comparavel || !c.metaKwh) return;
    const t = alvo[mes] ?? { realKwh: 0, metaKwh: 0, atingimento: null, estimada: false, comparavel: true };
    t.realKwh += c.realKwh;
    t.metaKwh = (t.metaKwh ?? 0) + c.metaKwh;
    t.estimada = t.estimada || c.estimada;
    t.atingimento = t.metaKwh > 0 ? t.realKwh / t.metaKwh : null;
    alvo[mes] = t;
  };

  for (const mes of meses) {
    const doMes = linhas.filter((l) => String(l.mes_ref ?? '').slice(0, 10) === mes);
    const porSite = new Map(doMes.map((l) => [l.site_id_api, l]));
    geracaoPorUsina(usinas, doMes, fatorKwhKwpDia).forEach((g, i) => {
      const u = usinas[i];
      const l = u.siteId ? porSite.get(u.siteId) : undefined;
      if (!l) return;
      const c: CelulaGeracao = {
        realKwh: g.realKwh,
        metaKwh: g.metaKwh,
        atingimento: g.atingimento,
        estimada: g.metaEstimada,
        comparavel: l.tem_baseline !== false && !(Number(l.baldes_nulos) > 0),
      };
      (porUsina[u.id] ??= {})[mes] = c;
      somar((porConjunto[u.conjunto] ??= {}), mes, c);
      somar(total, mes, c);
    });
  }
  return { meses, porUsina, porConjunto, total };
}

// ─────────────────────────── para onde vai a energia ───────────────────────────

export interface FluxoEnergia {
  usinaId: string;
  /** O que a usina produziu no mês (telemetria). */
  produzidoKwh: number | null;
  metaKwh: number | null;
  atingimento: number | null;
  /** O que a própria usina consumiu. */
  consumoProprioKwh: number;
  /** De onde veio o consumo próprio: a conta da usina, ou produzido − injetado. */
  consumoDaConta: boolean;
  /** O que os clientes do rateio usaram (compensaram nas contas). */
  usadoClientesKwh: number;
  /** O que sobrou e virou saldo de crédito. */
  sobrandoKwh: number;
  /** Clientes usaram mais do que a usina mandou no mês: gastaram saldo de meses anteriores. */
  tiradoDoSaldoKwh: number;
}

/**
 * "Quanto produziu, quanto a própria usina consumiu, quanto está mandando para
 * os outros e quanto está sobrando" — tudo como parte do que ela produziu.
 *
 *   produzido  = telemetria do mês (a mesma da meta)
 *   próprio    = consumo na conta da própria usina; sem a conta no sistema,
 *                produzido − injetado (a distribuidora só credita o que sobra)
 *   outros     = o que os clientes do rateio compensaram nas contas do mês
 *   sobrando   = o resto, que vira saldo de crédito
 *
 * ⚠️ É aproximado: a telemetria é do mês do calendário e as contas são do ciclo
 * de leitura de cada cliente. Serve para ver a ordem de grandeza (Morro Agudo 01
 * em SET/2026: produziu 154 MWh e os clientes usaram 1%), não para fechar kWh.
 */
export function fluxoEnergia(
  usinas: UsinaCeo[],
  financeiro: FinanceiroUsina[],
  geracaoDoMes: GeracaoUsina[],
): FluxoEnergia[] {
  return usinas.map((u, i) => {
    const f = financeiro.find((x) => x.usinaId === u.id);
    const g = geracaoDoMes[i];
    const produzido = g && !g.semTelemetria && g.realKwh > 0 ? g.realKwh : f?.injetadaKwh ?? null;
    const base = produzido ?? 0;
    const daConta = f?.consumoProprioKwh != null;
    const proprio = Math.min(base, daConta
      ? f!.consumoProprioKwh!
      : Math.max(0, base - (f?.injetadaKwh ?? base)));
    const disponivel = Math.max(0, base - proprio);
    const compensada = f?.compensadaKwh ?? 0;
    const usado = Math.min(compensada, disponivel);
    return {
      usinaId: u.id,
      produzidoKwh: produzido,
      metaKwh: g?.metaKwh ?? null,
      atingimento: g?.atingimento ?? null,
      consumoProprioKwh: proprio,
      consumoDaConta: daConta,
      usadoClientesKwh: usado,
      sobrandoKwh: Math.max(0, disponivel - usado),
      tiradoDoSaldoKwh: Math.max(0, compensada - disponivel),
    };
  });
}

// ─────────────────────────── projeção do mês ───────────────────────────

/** Um dia de geração de uma usina (RPC get_geracao_series, granularidade 'dia'). */
export interface DiaGeracao {
  site_id_api: string;
  bucket: string;
  geracao_kwh: number | null;
}

export interface ProjecaoUsina {
  usinaId: string;
  realKwh: number;
  /** Meta do mês INTEIRO (não rateada). */
  metaMesKwh: number | null;
  /** Média por dia nos últimos dias completos — "o jeito que as coisas estão agora". */
  ritmoKwhDia: number | null;
  /** Onde o mês fecha se o ritmo continuar. */
  projetadoKwh: number | null;
  /** projetado ÷ meta do mês: 0,92 = fecha em 92%. */
  projecao: number | null;
  /** Quanto falta para a meta do mês. */
  faltaKwh: number | null;
  /** Quanto precisaria gerar por dia, daqui até o fim do mês, para bater a meta. */
  precisaKwhDia: number | null;
  diasRestantes: number;
}

/**
 * "Se as coisas continuarem do jeito que estão, a meta do mês será batida?"
 *
 * O percentual de hoje (90% da meta até hoje) diz como o mês ANDOU; a projeção
 * diz como ele vai FECHAR. O ritmo é a média dos últimos dias completos (7, na
 * tela), não a do mês inteiro: uma parada no dia 6 já passou e não deve puxar
 * o fim do mês para baixo, mas um inversor fora desde o dia 4 continua puxando.
 *
 * A meta do mês inteiro é a do laudo (`meta_cheia_kwh`); para usina sem laudo,
 * a mesma produtividade de Vera Cruz no mês cheio, vezes a potência dela.
 */
export function projetarMes(
  usinas: UsinaCeo[],
  linhasMes: LinhaVsMeta[],
  diarios: DiaGeracao[],
  diasNoMes: number,
  fatorKwhKwpDia: number,
): ProjecaoUsina[] {
  const porSite = new Map(linhasMes.map((l) => [l.site_id_api, l]));
  // Produtividade do mês cheio nas usinas com laudo, para emprestar às outras.
  let metaLaudo = 0;
  let kwpLaudo = 0;
  for (const u of usinas) {
    const l = u.siteId ? porSite.get(u.siteId) : undefined;
    if (l && Number(l.meta_cheia_kwh) > 0 && u.capacidadeKwp) { metaLaudo += Number(l.meta_cheia_kwh); kwpLaudo += u.capacidadeKwp; }
  }
  const produtividadeMes = kwpLaudo > 0 ? metaLaudo / kwpLaudo : null;

  const ritmos = new Map<string, number[]>();
  for (const d of diarios) {
    if (d.geracao_kwh == null) continue;
    const lista = ritmos.get(d.site_id_api) ?? [];
    lista.push(Number(d.geracao_kwh));
    ritmos.set(d.site_id_api, lista);
  }

  return usinas.map((u) => {
    const l = u.siteId ? porSite.get(u.siteId) : undefined;
    const real = Number(l?.geracao_kwh) || 0;
    const decorridos = Math.max(0, Number(l?.dias_decorridos) || 0);
    const restantes = Math.max(0, diasNoMes - decorridos);
    const metaMes = Number(l?.meta_cheia_kwh) > 0
      ? Number(l!.meta_cheia_kwh)
      : u.capacidadeKwp
        ? produtividadeMes != null ? u.capacidadeKwp * produtividadeMes : fatorKwhKwpDia > 0 ? u.capacidadeKwp * fatorKwhKwpDia * diasNoMes : null
        : null;
    const dias = u.siteId ? ritmos.get(u.siteId) : undefined;
    const ritmo = dias && dias.length ? dias.reduce((t, v) => t + v, 0) / dias.length : null;
    const projetado = l && ritmo != null ? real + ritmo * restantes : null;
    const falta = metaMes != null ? Math.max(0, metaMes - real) : null;
    return {
      usinaId: u.id,
      realKwh: real,
      metaMesKwh: metaMes,
      ritmoKwhDia: ritmo,
      projetadoKwh: projetado,
      projecao: projetado != null && metaMes ? projetado / metaMes : null,
      faltaKwh: falta,
      precisaKwhDia: falta != null && restantes > 0.01 ? falta / restantes : null,
      diasRestantes: restantes,
    };
  });
}

/** A projeção de um grupo de usinas (conjunto ou todas): soma o que dá para somar. */
export function totalProjecao(lista: ProjecaoUsina[]) {
  const validas = lista.filter((p) => p.projetadoKwh != null && p.metaMesKwh);
  const real = validas.reduce((t, p) => t + p.realKwh, 0);
  const meta = validas.reduce((t, p) => t + (p.metaMesKwh ?? 0), 0);
  const projetado = validas.reduce((t, p) => t + (p.projetadoKwh ?? 0), 0);
  const ritmo = validas.reduce((t, p) => t + (p.ritmoKwhDia ?? 0), 0);
  const restantes = validas.length ? Math.max(...validas.map((p) => p.diasRestantes)) : 0;
  const falta = Math.max(0, meta - real);
  return {
    realKwh: real,
    metaMesKwh: meta,
    projetadoKwh: projetado,
    projecao: meta > 0 ? projetado / meta : null,
    ritmoKwhDia: ritmo,
    faltaKwh: falta,
    precisaKwhDia: restantes > 0.01 ? falta / restantes : null,
    diasRestantes: restantes,
  };
}

export interface DiaDinheiro {
  /** "YYYY-MM-DD". */
  dia: string;
  /** Soma dos boletos que venceram até este dia. */
  venceuAcum: number;
  /** Desses, os já pagos. Até hoje é fato; depois de hoje inclui só o que já foi pago adiantado. */
  pagoAcum: number;
  /** Só de hoje em diante: pago + o que vai vencer até o dia × taxa de recebimento. */
  previsaoAcum: number | null;
}

/**
 * O mês dia a dia, acumulado, para o gráfico de um mês só: a linha do que VENCE,
 * a do que foi PAGO, e daqui para a frente a PREVISÃO. A distância entre "venceu"
 * e "pago" até hoje é o atraso; onde a previsão termina é onde o mês fecha.
 *
 * Usa as mesmas regras de `calcularDinheiroDoPeriodo` — no último dia, venceu =
 * esperado e previsão = previsao do resumo.
 */
export function serieDiariaDinheiro(
  entrada: FaturaPainel[],
  inicio: string,
  fim: string,
  hoje: Date,
  taxaRecebimento: number,
  limitePrevisao: string | null,
): DiaDinheiro[] {
  const hojeIso = isoLocal(hoje);
  const venceu = new Map<string, number>();
  const pago = new Map<string, number>();
  const aVencer = new Map<string, number>();
  for (const f of faturasDoPainel(entrada)) {
    if (!ehCobranca(f) || f.statusDerivado === 'Parcelado') continue;
    const v = String(dataVencimentoDe(f) ?? '').slice(0, 10);
    if (!v || v < inicio || v > fim) continue;
    const valor = valorDevido(f);
    if (estaPago(f)) pago.set(v, (pago.get(v) ?? 0) + valor);
    else if (f.statusDerivado === 'Em aberto') {
      if (!limitePrevisao || v <= limitePrevisao) aVencer.set(v, (aVencer.get(v) ?? 0) + valor);
    } else if (f.statusDerivado !== 'Vencido') continue;
    venceu.set(v, (venceu.get(v) ?? 0) + valor);
  }

  const saida: DiaDinheiro[] = [];
  let acV = 0, acP = 0, acF = 0;
  const [a, m, d] = inicio.split('-').map(Number);
  for (let dt = new Date(a, m - 1, d); isoLocal(dt) <= fim; dt = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() + 1)) {
    const dia = isoLocal(dt);
    acV += venceu.get(dia) ?? 0;
    acP += pago.get(dia) ?? 0;
    if (dia >= hojeIso) acF += (aVencer.get(dia) ?? 0) * taxaRecebimento;
    saida.push({ dia, venceuAcum: acV, pagoAcum: acP, previsaoAcum: dia >= hojeIso ? acP + acF : null });
  }
  return saida;
}
